import { Skeleton, ToggleButtonGroup, Typography } from '@mui/material';
import { FilterField, toggleGroupSx } from '@retail/components';
import { MyAssortmentMessageKey, useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { HierarchyLevels } from '@retail/products/types';
import { LevelToggleButton } from './LevelToggleButton';
import { PackageType } from '@retail/calculus/types';
import { useMemo } from 'react';

interface Props {
  existingLevels?: HierarchyLevels[];
  availablePackageTypes?: PackageType[];
  selectedHierarchyLevel?: HierarchyLevels | PackageType;
  isLoading: boolean;
  setSelectedHierarchyLevel: (level?: HierarchyLevels | PackageType) => void;
}

interface LevelLabels {
  name: MyAssortmentMessageKey;
  short: MyAssortmentMessageKey;
}

const levelLabels: Record<HierarchyLevels, LevelLabels> = {
  ASSORTMENT: {
    name: 'myAssortment.levels.assortment',
    short: 'myAssortment.levels.assortmentShort',
  },
  MG_PARTICIPANT_ACCOUNT: {
    name: 'myAssortment.levels.supplier',
    short: 'myAssortment.levels.supplierShort',
  },
  OVERGRUPPE: {
    name: 'myAssortment.levels.overGroup',
    short: 'myAssortment.levels.overGroupShort',
  },
  HOVEDGRUPPE: {
    name: 'myAssortment.levels.mainGroup',
    short: 'myAssortment.levels.mainGroupShort',
  },
  VAREGRUPPE: {
    name: 'myAssortment.levels.productGroup',
    short: 'myAssortment.levels.productGroupShort',
  },
  DISCOUNT_GROUP: {
    name: 'myAssortment.levels.discountGroup',
    short: 'myAssortment.levels.discountGroupShort',
  },
  MG_SUPPLIER_ITEM: { name: 'myAssortment.levels.item', short: 'myAssortment.levels.itemShort' },
};

const packageTypeLabels = {
  SECONDARY: { name: 'myAssortment.levels.secondary', short: 'myAssortment.levels.secondaryShort' },
  TERTIARY: { name: 'myAssortment.levels.tertiary', short: 'myAssortment.levels.tertiaryShort' },
} as const satisfies Partial<Record<PackageType, LevelLabels>>;

const orderedLevels = Object.keys(levelLabels) as HierarchyLevels[];
const orderedPackageTypes = Object.keys(packageTypeLabels) as (keyof typeof packageTypeLabels)[];

export function LevelToggleButtonGroup({
  existingLevels,
  availablePackageTypes,
  selectedHierarchyLevel,
  isLoading,
  setSelectedHierarchyLevel,
}: Props) {
  const t = useMyAssortmentTFunction();

  const buttons = useMemo(() => {
    const nrOfLevels = existingLevels?.length ?? 0;
    return [
      ...orderedLevels
        .filter((level) => existingLevels?.includes(level))
        .map((level) => ({
          key: level,
          level,
          packageType: undefined,
          nrOfLevels,
          ...levelLabels[level],
        })),
      ...orderedPackageTypes
        .filter((packageType) => availablePackageTypes?.includes(packageType))
        .map((packageType) => ({
          key: packageType,
          level: 'MG_SUPPLIER_ITEM' as const,
          packageType,
          nrOfLevels,
          ...packageTypeLabels[packageType],
        })),
    ];
  }, [existingLevels, availablePackageTypes]);

  const isPending = (!existingLevels && !availablePackageTypes) || isLoading;

  return (
    <FilterField label={t('myAssortment.filter.level')}>
      {isPending ? (
        <Skeleton height={40} width="100%" variant="rectangular" />
      ) : buttons.length === 0 ? (
        <Typography variant="body3" pl={1}>
          {t('myAssortment.filter.noLevels')}
        </Typography>
      ) : (
        <ToggleButtonGroup
          size="small"
          sx={[
            toggleGroupSx,
            {
              width: 'fit-content',
              flexWrap: 'wrap',
              '& .MuiToggleButtonGroup-grouped::after': { display: 'none' },
            },
          ]}
          exclusive
          value={selectedHierarchyLevel}
          onChange={(_, value) =>
            setSelectedHierarchyLevel(selectedHierarchyLevel === value ? undefined : value)
          }
        >
          {buttons.map(({ key, level, packageType, nrOfLevels, name, short }) => (
            <LevelToggleButton
              key={key}
              level={level}
              packageType={packageType}
              nrOfLevels={nrOfLevels}
              levelName={t(name)}
              levelNameShort={t(short)}
            />
          ))}
        </ToggleButtonGroup>
      )}
    </FilterField>
  );
}
